import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink, Info } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { crmApi } from '@/lib/crmApi';
import { fmt, fmtMoney, fmtPct } from '@/components/stats/StatsShared';

function orderEditPath(orderId) {
  return `/crm/customer-orders/${orderId}/edit`;
}

function formatOrderDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU');
}

function OrderLink({ orderId, orderNumber, className = '' }) {
  return (
    <Link
      to={orderEditPath(orderId)}
      className={`inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-800 hover:underline ${className}`}
    >
      {orderNumber || `#${orderId}`}
      <ExternalLink className="w-3 h-3 shrink-0" />
    </Link>
  );
}

function formatSupplierPurchase(line) {
  if (line.supplier_currency === 'RUB') {
    return (
      <div className="text-right">
        <div>{fmt(line.supplier_price)} ₽</div>
        <div className="text-gray-400">
          → {fmtMoney(line.supplier_price_kzt)}
          {line.rub_to_kzt_rate ? ` (×${line.rub_to_kzt_rate})` : ''}
        </div>
      </div>
    );
  }
  return <span>{fmtMoney(line.supplier_price)}</span>;
}

function ProductOrderLinesTable({ lines, loading, error }) {
  if (loading) {
    return <p className="text-gray-400 text-xs py-2">Загрузка заказов…</p>;
  }
  if (error) {
    return <p className="text-red-500 text-xs py-2">{error}</p>;
  }
  if (!lines?.length) {
    return <p className="text-gray-400 text-xs py-2">Нет строк заказов с закупочной ценой</p>;
  }

  return (
    <div className="mt-3 overflow-x-auto rounded-md border border-slate-200 bg-white">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/80">
            <th className="text-left py-2 px-2 text-gray-500 font-medium">Заказ</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Кол-во</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Цена</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Скидка</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Закупка</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Итого</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Прибыль</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Наценка</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.item_id} className="border-b border-slate-50 last:border-0">
              <td className="py-2 px-2 align-top">
                <OrderLink orderId={line.order_id} orderNumber={line.order_number} />
                <p className="text-gray-400 mt-0.5">{formatOrderDate(line.order_date)}</p>
                <p className="text-gray-400">{line.order_status_label}</p>
              </td>
              <td className="py-2 px-2 text-right text-gray-700">{fmt(line.quantity)}</td>
              <td className="py-2 px-2 text-right text-gray-700">{fmtMoney(line.price)}</td>
              <td className="py-2 px-2 text-right text-gray-600">
                {line.discount_percent > 0 ? fmtPct(line.discount_percent) : '—'}
              </td>
              <td className="py-2 px-2 text-right text-orange-700">{formatSupplierPurchase(line)}</td>
              <td className="py-2 px-2 text-right font-medium text-gray-800">{fmtMoney(line.total)}</td>
              <td className="py-2 px-2 text-right font-semibold text-emerald-700">{fmtMoney(line.profit)}</td>
              <td className="py-2 px-2 text-right align-top">
                <p className="font-semibold text-indigo-700">+{fmtPct(line.markup_percent)}</p>
                <p className="text-gray-400">маржа {fmtPct(line.margin_percent)}</p>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientOrderLinesTable({ orders, loading, error }) {
  if (loading) {
    return <p className="text-gray-400 text-xs py-2">Загрузка заказов…</p>;
  }
  if (error) {
    return <p className="text-red-500 text-xs py-2">{error}</p>;
  }
  if (!orders?.length) {
    return <p className="text-gray-400 text-xs py-2">Нет заказов с данными о закупке</p>;
  }

  return (
    <div className="mt-3 overflow-x-auto rounded-md border border-slate-200 bg-white">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/80">
            <th className="text-left py-2 px-2 text-gray-500 font-medium">Заказ</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Поз.</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Выручка</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Себест.</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Прибыль</th>
            <th className="text-right py-2 px-2 text-gray-500 font-medium">Наценка</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.order_id} className="border-b border-slate-50 last:border-0">
              <td className="py-2 px-2 align-top">
                <OrderLink orderId={order.order_id} orderNumber={order.order_number} />
                <p className="text-gray-400 mt-0.5">{formatOrderDate(order.order_date)}</p>
                <p className="text-gray-400">{order.order_status_label}</p>
              </td>
              <td className="py-2 px-2 text-right text-gray-600">{fmt(order.items_count)}</td>
              <td className="py-2 px-2 text-right text-gray-700">{fmtMoney(order.revenue)}</td>
              <td className="py-2 px-2 text-right text-orange-700">{fmtMoney(order.cost)}</td>
              <td className="py-2 px-2 text-right font-semibold text-emerald-700">{fmtMoney(order.profit)}</td>
              <td className="py-2 px-2 text-right align-top">
                <p className="font-semibold text-indigo-700">+{fmtPct(order.markup_percent)}</p>
                <p className="text-gray-400">маржа {fmtPct(order.margin_percent)}</p>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BreakdownToggle({ open, onToggle, label = 'Как считается?' }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-1"
    >
      {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      {open ? 'Скрыть расшифровку' : label}
    </button>
  );
}

function BreakdownPanel({ children }) {
  return (
    <div className="mt-2 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 text-xs text-gray-600 space-y-1.5 leading-relaxed">
      {children}
    </div>
  );
}

function FormulaLine({ label, value, hint }) {
  return (
    <div>
      <span className="text-gray-500">{label}: </span>
      <span className="font-medium text-gray-800">{value}</span>
      {hint && <p className="text-gray-400 mt-0.5 pl-0">{hint}</p>}
    </div>
  );
}

/** Наценка от закупки — то, как вы считаете «+70% к закупу». */
export function MarkupMarginHint({ markup, margin, className = '' }) {
  return (
    <span className={`text-xs ${className}`}>
      <span className="text-indigo-700 font-semibold">+{fmtPct(markup)}</span>
      <span className="text-gray-400"> к закупу</span>
      {margin != null && (
        <>
          <span className="text-gray-300"> · </span>
          <span className="text-gray-500">маржа {fmtPct(margin)}</span>
        </>
      )}
    </span>
  );
}

export function ProfitMethodologyNote({ rubToKztRate }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/50 px-4 py-3">
      <div className="flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800">Методика расчёта прибыльности</p>
          <p className="text-xs text-gray-600 mt-1">
            Себестоимость считается в тенге. Если поставщик в рублях — закупочная цена умножается на курс из настроек
            {rubToKztRate ? ` (сейчас 1 ₽ = ${rubToKztRate} ₸)` : ''}.
            Основной показатель — <strong>наценка от закупки</strong> («+70% к закупу»).
          </p>
          <BreakdownToggle open={open} onToggle={() => setOpen((v) => !v)} />
          {open && (
            <BreakdownPanel>
              <FormulaLine
                label="Выручка по строке"
                value="поле «Итого» позиции (цена продажи с учётом скидки × кол-во)"
              />
              <FormulaLine
                label="Себестоимость по строке"
                value="закупочная цена × кол-во; для RUB — сначала × курс RUB→KZT из настроек"
              />
              <FormulaLine
                label="Прибыль"
                value="выручка − себестоимость"
              />
              <FormulaLine
                label="Наценка от закупки"
                value="прибыль / себестоимость × 100% — ваш «+70% к закупу»"
                hint="Пример: закуп 4 459 ₸, продажа 7 500 ₸ → прибыль 3 041 ₸ → наценка +68,2%"
              />
              <FormulaLine
                label="Маржа от продажи"
                value="прибыль / выручка × 100% — доля прибыли в цене для покупателя"
                hint="Тот же пример: 3 041 / 7 500 = 40,5% — это не наценка к закупу"
              />
              <p className="text-gray-400 pt-1">
                Продажа чуть ниже «4 459 + 70% = 7 580» (скидка/округление) — поэтому наценка ~68%, а не ровно 70%.
              </p>
            </BreakdownPanel>
          )}
        </div>
      </div>
    </div>
  );
}

export function SummaryMetricBreakdown({ metric, data }) {
  const [open, setOpen] = useState(false);

  const configs = {
    revenue: {
      title: 'Выручка (с закупкой)',
      lines: [
        { label: 'Формула', value: 'Σ «Итого» по позициям, где есть закупочная цена' },
        { label: 'Результат', value: fmtMoney(data.revenue_with_cost) },
        {
          label: 'Охват',
          value: `${fmt(data.items_with_cost)} из ${fmt(data.items_total)} позиций (${fmtPct(data.cost_coverage_percent)})`,
          hint: 'Позиции без закупочной цены в расчёт не входят',
        },
      ],
    },
    cost: {
      title: 'Себестоимость',
      lines: [
        { label: 'Формула', value: 'Σ (закупочная цена × кол-во) по тем же позициям' },
        { label: 'Результат', value: fmtMoney(data.total_cost) },
        {
          label: 'Источник закупки',
          value: 'снимок supplier_price_snapshot; валюта — из поставщика/товара в каталоге',
        },
        {
          label: 'Курс RUB → KZT',
          value: data.rub_to_kzt_rate ? `1 ₽ = ${data.rub_to_kzt_rate} ₸ (из настроек)` : 'из настроек системы',
        },
      ],
    },
    profit: {
      title: 'Валовая прибыль',
      lines: [
        { label: 'Формула', value: 'выручка − себестоимость' },
        {
          label: 'Подстановка',
          value: `${fmtMoney(data.revenue_with_cost)} − ${fmtMoney(data.total_cost)} = ${fmtMoney(data.gross_profit)}`,
        },
      ],
    },
    margin: {
      title: 'Наценка и маржа',
      lines: [
        {
          label: 'Наценка от закупки',
          value: `прибыль / себестоимость × 100% = ${fmtPct(data.markup_percent)}`,
          hint: 'Как «+70% к закупу» — сколько заработали относительно закупочной цены',
        },
        {
          label: 'Маржа от продажи',
          value: `прибыль / выручка × 100% = ${fmtPct(data.margin_percent)}`,
          hint: 'Доля прибыли в цене для покупателя — всегда ниже наценки при типичных процентах',
        },
        {
          label: 'Подстановка',
          value: `${fmtMoney(data.gross_profit)} / ${fmtMoney(data.total_cost)} = ${fmtPct(data.markup_percent)} наценка · ${fmtMoney(data.gross_profit)} / ${fmtMoney(data.revenue_with_cost)} = ${fmtPct(data.margin_percent)} маржа`,
        },
      ],
    },
  };

  const cfg = configs[metric];
  if (!cfg) return null;

  return (
    <div className="mt-2">
      <BreakdownToggle open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <BreakdownPanel>
          <p className="font-medium text-gray-700 mb-1">{cfg.title}</p>
          {cfg.lines.map((line) => (
            <FormulaLine key={line.label} {...line} />
          ))}
        </BreakdownPanel>
      )}
    </div>
  );
}

export function ProductProfitBreakdown({ product, compact = false }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const p = product;

  useEffect(() => {
    if (!open || !token) return undefined;
    if (lines !== null) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    crmApi
      .getProductProfitOrderLines(token, {
        product_id: p.product_id || undefined,
        product_code: p.product_code || undefined,
      })
      .then((data) => {
        if (!cancelled) setLines(data.lines || []);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить заказы');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, token, p.product_id, p.product_code, lines]);

  const handleToggle = () => setOpen((v) => !v);

  return (
    <div className={compact ? '' : 'border-b border-gray-50 pb-3 last:border-0'}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {!compact && (
            <>
              <p className="text-sm font-medium text-gray-900 truncate">{p.product_name}</p>
              <p className="text-xs text-gray-400">
                {p.product_code} · {fmt(p.total_qty)} шт · {fmt(p.orders_count)} зак.
                {!compact && p.margin_percent != null && ` · маржа ${fmtPct(p.margin_percent)}`}
              </p>
            </>
          )}
          <BreakdownToggle open={open} onToggle={handleToggle} />
        </div>
        {!compact && (
          <span className="text-sm font-semibold text-emerald-700 shrink-0">{fmtMoney(p.profit)}</span>
        )}
      </div>

      {open && (
        <BreakdownPanel>
          <FormulaLine
            label="Выручка"
            value={`${fmtMoney(p.revenue)} = ${fmt(p.total_qty)} шт × ~${fmtMoney(p.avg_sale_price)}/шт`}
            hint="Сумма полей «Итого» по всем строкам этого товара в заказах покупателей"
          />
          <FormulaLine
            label="Себестоимость"
            value={`${fmtMoney(p.cost)} = ${fmt(p.total_qty)} шт × ~${fmtMoney(p.avg_purchase_price)}/шт в ₸`}
            hint="Для поставщиков в рублях закупка уже пересчитана по курсу из настроек"
          />
          <FormulaLine
            label="Прибыль"
            value={`${fmtMoney(p.profit)} = ${fmtMoney(p.revenue)} − ${fmtMoney(p.cost)}`}
          />
          <FormulaLine
            label="Маржа от продажи"
            value={`${fmtPct(p.margin_percent)} = ${fmtMoney(p.profit)} / ${fmtMoney(p.revenue)} × 100%`}
            hint="Доля прибыли в цене для покупателя"
          />
          <FormulaLine
            label="Наценка от закупки"
            value={`+${fmtPct(p.markup_percent)} = ${fmtMoney(p.profit)} / ${fmtMoney(p.cost)} × 100%`}
            hint="Ваш способ: «сколько % добавили к закупочной цене»"
          />
          <FormulaLine
            label="Прибыль на единицу"
            value={`~${fmtMoney(p.profit_per_unit)}/шт`}
          />
          <FormulaLine
            label="Заказов с товаром"
            value={fmt(p.orders_count)}
          />

          <p className="font-medium text-gray-700 pt-2 border-t border-slate-200 mt-2">
            Строки заказов — нажмите на номер, чтобы проверить цену и скидку
          </p>
          <ProductOrderLinesTable lines={lines} loading={loading} error={error} />
        </BreakdownPanel>
      )}
    </div>
  );
}

export function ClientProfitBreakdown({ client }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const c = client;

  useEffect(() => {
    if (!open || !token) return undefined;
    if (orders !== null) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    crmApi
      .getClientProfitOrderLines(token, c.client_id)
      .then((data) => {
        if (!cancelled) setOrders(data.orders || []);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить заказы');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, token, c.client_id, orders]);

  return (
    <div>
      <BreakdownToggle open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <BreakdownPanel>
          <FormulaLine
            label="Выручка клиента"
            value={fmtMoney(c.revenue)}
            hint={`По ${fmt(c.orders_count)} заказам — сумма «Итого» позиций с закупочной ценой`}
          />
          <FormulaLine label="Себестоимость" value={fmtMoney(c.cost)} />
          <FormulaLine
            label="Прибыль"
            value={`${fmtMoney(c.profit)} = ${fmtMoney(c.revenue)} − ${fmtMoney(c.cost)}`}
          />
          <FormulaLine
            label="Маржа от продажи"
            value={`${fmtPct(c.margin_percent)} = ${fmtMoney(c.profit)} / ${fmtMoney(c.revenue)} × 100%`}
          />
          <FormulaLine
            label="Наценка от закупки"
            value={`+${fmtPct(c.markup_percent)} = ${fmtMoney(c.profit)} / ${fmtMoney(c.cost)} × 100%`}
            hint="Близко к вашему «+70% к закупу», если продали около закуп × 1,7"
          />

          <p className="font-medium text-gray-700 pt-2 border-t border-slate-200 mt-2">
            Заказы клиента
          </p>
          <ClientOrderLinesTable orders={orders} loading={loading} error={error} />
        </BreakdownPanel>
      )}
    </div>
  );
}

export function SourceProfitBreakdown({ source }) {
  const [open, setOpen] = useState(false);
  const s = source;

  return (
    <div className="w-full mt-1">
      <BreakdownToggle open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <BreakdownPanel>
          <FormulaLine
            label="Заказов"
            value={fmt(s.orders)}
            hint="Заказы покупателей с данным источником (WhatsApp, магазин и т.д.)"
          />
          <FormulaLine label="Выручка" value={fmtMoney(s.revenue)} />
          <FormulaLine label="Себестоимость" value={fmtMoney(s.cost)} />
          <FormulaLine
            label="Прибыль"
            value={`${fmtMoney(s.profit)} = ${fmtMoney(s.revenue)} − ${fmtMoney(s.cost)}`}
          />
          <FormulaLine label="Наценка от закупки" value={`+${fmtPct(s.markup_percent)}`} />
          <FormulaLine label="Маржа от продажи" value={`${fmtPct(s.margin_percent)}`} />
        </BreakdownPanel>
      )}
    </div>
  );
}

export function MonthProfitBreakdown({ month }) {
  const [open, setOpen] = useState(false);
  const m = month;

  return (
    <div>
      <BreakdownToggle open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <BreakdownPanel>
          <FormulaLine label="Выручка за месяц" value={fmtMoney(m.revenue)} />
          <FormulaLine label="Себестоимость" value={fmtMoney(m.cost)} />
          <FormulaLine
            label="Прибыль"
            value={`${fmtMoney(m.profit)} = ${fmtMoney(m.revenue)} − ${fmtMoney(m.cost)}`}
          />
          <FormulaLine label="Наценка от закупки" value={`+${fmtPct(m.markup_percent)}`} />
          <FormulaLine label="Маржа от продажи" value={`${fmtPct(m.margin_percent)}`} />
        </BreakdownPanel>
      )}
    </div>
  );
}

export function SectionCriteriaNote({ children }) {
  return <p className="text-xs text-gray-400 mt-1">{children}</p>;
}
